export interface ETLReportingSetObj {
    _id: string;
    ReportingSetName: string;
    DisplayName?: string;
    ForReports?: boolean;
    MaxReportDays?: number;
    CanExport?: boolean;
    ReportingSetGroup: string;
    ReportingSetDescription?: string;
    BaseFeed: string;
    Joins: object[];
    DataFields: object;
    Dates?: object;
    ExcludeIf?: object;
    ContactHierarchy: object;
    EmpHierarchy: object;
    Status: Partial<Status>;
    IsEditable: boolean;
}

export interface DataList {
    field1?: ETLJoinedField;
    field2?: ETLJoinedField;
    isActive?: boolean;
    isKey?: boolean;
    dropdownOptions?: string;
    Name: string;
    DataType: number;
}

export interface ETLJoinedField {
    dropdownOptions: string;
    Name: string;
    DataType: number;
    isKey: boolean;
    isActive: boolean;
}

export interface Status {
    IsValid: boolean;
    ConsecutiveFailureCount: number;
    LastSuccessDate: string;
    LastFailureDate: string;
}

export interface ETLBasefeed {
    dropdownOptions?: string;
    feed2?: ETLJoinedFeed;
    feed1?: ETLJoinedFeed;
    SourceSystem?: string;
    id: string | number;
    name: string;
    FeedName?: string;
    DataList?: ETLFeedDataList[];
}

export interface ETLJoinedFeed {
    dropdownOptions: string;
    id: number;
    Name?: string;
    name?: string;
    FeedName?: string;
    SourceSystem?: string;
    DataList?: DataList[];
};

export interface ETLFeedDataList {
    isActive: boolean;
    Name: string;
    DataType: number;
}

export interface ETLReportingSetJoinedFeedsList {
    joinKeyMap?: number;
    joinedFeedName?: string;
    checkbox?: boolean;
    JoinID?: number;
    feed1?: ETLJoinedFeed;
    feed2?: ETLJoinedFeed;
    joinedFields?: DataList[];
    filterComparedSText?: {
        field1: string[];
        field2: string[];
    };
    id?: number;
}

export enum ReportingSetSections {
    LIST,
    CREATE_EDIT,
    FLOW_BUILDER
}

export interface RowDataObj {
    [key: string]: any;
}

export interface ETLReportingSetDropdownOptions {
    id: string | number;
    label: string;
    type?: string;
    joinKeyMap?: number;
    value?: string;
    orderId?: number;
    Name?: string
}

export interface ETLGroup {
    group: string;
    selectedItem: string;
    saveItem?: object;
    contactSaveItem?: object;
}

export enum ETLReportingSetFieldTypes {
    SIMPLE = "Simple",
    CONDITIONAL = "Conditional",
    COMPLEX_STRING = "Complex String",
    SUBSTRING = "Substring",
    COMPLEX_DATE = "Complex Date",
    COMPLEX_NUMERIC = "Complex Numeric",
    REGEX = "Regex",
    COALESCE = "Coalesce",
    COMPARISONS = "comparisons",
    COMPLEX_NUMERIC_STRING = "ComplexNumeric",
    COMPLEX_DATE_STRING = "ComplexDate"
}

export enum ETLReportingSetReturnTypes {
    TEXT,
    WHOLE_NUMBER,
    DECIMAL_NUMBER,
    DATE,
    TRUE_FALSE,
}

export enum ETLReportingSetSimpleFieldReturnOptions {
    DEFAULT,
    SPECIFIC_FIELD,
    DEFAULT_VALUE,
    SPECIFIC_FIELD_AND_DEFAULT_VALUE,
}

export enum ETLReportingSetFlowbuilderModules {
    JOINED_FEEDS = 'joinedFeeds',
    DATES = 'dates',
    DATA_FIELDS = 'dataFields',
    ATTRIBUTES = 'attributes',
    EMPLOYEE_GROUP = 'employeeGroup',
    CONTACT_GROUP = 'contactGroup',
    EXCLUDE_IF = 'excludeIf',
    DEFAULT = 'default',
    TYPE_SIMPLE = 'Simple'
}

export const ETLReportingSetFunctionTypes = {
    "No Function": 'object:null',
    Abs: 'number:0',
    Cos: 'number:1',
    Cosh: 'number:2',
    Arccos: 'number:3',
    Sin: 'number:4',
    Sinh: 'number:5',
    Arcsin: 'number:6',
    Tan: 'number:7',
    Tanh: 'number:8',
    Arctan: 'number:9',
    Sqrt: 'number:10',
    Exp: 'number:11',
    Log: 'number:12',
    Round: 'number:13',
    Truncate: 'number:14',
    Floor: 'number:15',
    Ceiling: 'number:16',
    Sign: 'number:17',
}

export enum ETLReportingSetOperationTypes {
    Modulus = 'number:0',
    Exponent = 'number:1',
    Divide = 'number:2',
    Multiply = 'number:3',
    Minus = 'number:4',
    Plus = 'number:5',
    Greater_Than = 'number:6',
    Less_Than = 'number:7',
    Equal_To = 'number:8',
}

export enum ETLReportingSetDateOperationTypes {
    ADD_SUBTRACT_UNIT,
    DATE_DIFFERENCE
}

export enum ETLReportingSetUnitsOptions {
    MILLISECONDS,
    SECONDS,
    MINUTES,
    HOURS,
    DAYS,
    MONTH
}

export enum ETLReportingSetComparisonTypes {
    GreaterThan,
    GreaterThanOrEqualTo,
    LessThan,
    LessThanOrEqualTo,
    EqualTo,
    IsNull,
    IsNotNull,
    IsNullOrEmpty,
    IsNotNullAndIsNotEmpty,
    IsTrue,
    IsFalse,
    NotEqualTo
}

export const PERMISSION = {
    VIEW: "VIEW",
    CREATE: "CREATE"
};

export interface ContactGroupPayload {
    reportingSetData: object;
    contactGroupData: HierarchyGroup;
    selectedSection?: string;
}

export interface HierarchyGroupDataObj {
    groupLevel: string;
    hierarchyDataObject: HierarchyDataObj;
}

export interface HierarchyGroup {
    Assignable: boolean;
    BaseGroupType: number;
    CompanyID: number;
    GroupLevel: number;
    GroupTypeName: string;
}

export interface HierarchyDataObjValue {
    JoinID: number;
    Field: string;
    DefaultValue: number;
    IsNull: boolean;
    ExpectedType: number;
}

export interface HierarchyDataObj {
    [key: string]: HierarchyDataObjValue;
}

export interface EmployeeGroupPayload {
    reportingSetData: object;
    employeeHierarchyData: HierarchyGroup;
    selectedSection?: string;
}

export interface ETLSimpleFieldType {
    return: object;
    JoinID: object;
    IsNull?: boolean;
    DefaultValue?: string;
    Field?: object;
    isValid?: boolean;
}

export interface ReportingSetFieldPayload {
    Mode: number;
    QueuedOnImport: boolean;
    Type: number;
    Dates: object;
    Attributes: object;
    ExcludeIf: object;
    IsApiSourced: boolean;
    IsEditable: boolean;
    IsViewable: boolean;
    IsTemplate: boolean;
    BaseSourceSystem: string;
    TimeToLive: number;
    preexisting: boolean;
    lastRunDisplay: string;
    lastRunCellClass: object;
}

export interface ParentOutputFormType {
    [key: string]: object;
}

export interface DatesParentDataType {
    type?: string;
    name?: string;
    uniqueId?: number;
    parentId?: number;
    childs?: object[];
    simpleFieldType?: ETLSimpleFieldType;
    isValid?: boolean;
}

export interface DefaultConfigurationData {
    fieldName?: string;
    returnDropdownOption?: {
        id: number;
        label: string;
    };
}

export interface ComparisonObject {
    comparisonType: ETLReportingSetDropdownOptions;
    field1: Array<DatesParentDataType>;
    field2: Array<DatesParentDataType>;
}

export interface SimpleFieldModalType {
    FIELDS: string;
    "DATA TYPE": string;
    SOURCE?: string;
    "FEED NAME"?: string;
    JOINNAME?: string;
    JOINID?: number;
}

export interface FunctionComponentType {
    childs: Array<object>;
    functionType: { id: string; label: string; } | string;
    numericType: string;
    operations: { id: string; label: string; } | string;
}

export interface FunctionPayloadType {
    FieldExpression: object;
    Function: number;
    Operation: number;
    isEven: boolean;
    isFirst: boolean;
}

export enum ETLReportingSetFieldTypesId {
    SIMPLE,
    CONDITIONAL,
    COMPLEX_STRING,
    SUBSTRING,
    COMPLEX_DATE,
    COMPLEX_NUMERIC,
    REGEX,
    COALESCE
}

export interface EmployeeGroupListType {
    GroupTypeName: string;
    GroupData: object;
}

export interface ETLExcludeIf {
    isAdd: boolean;
    source: string;
}

export enum ComplexNumericFunctionDropdownId {
    NoFunction,
    Abs,
    Cos,
    Cosh,
    Arccos,
    Sin,
    Sinh,
    Arcsin,
    Tan,
    Tanh,
    Arctan,
    Sqrt,
    Exp,
    Log,
    Round,
    Truncate,
    Floor,
    Ceiling,
    Sign,
}

export enum ETLPayloadTypes {
    DATES = 'dates',
    DATAFIELDS = 'datafields'
}

export enum STATUS {
    SUCCESS = 'success',
    FAILURE = 'failure'
}

export enum HierarchyGroupName {
    COMPANY = "Company",
    TEAM = "Team",
    AGENT = "Agent",
    CAMPAIGN = "Campaign",
    SKILL = "Skill",
    POINT_OF_CONTACT = "PointofContact"
}

export const ComparisonTypes = ['>', '>=', '<', '<=', '=', 'Is Null', 'Is Not Null', 'Is Null Or Empty',
    'Is Not Null And Is Not Empty', 'Is True', 'Is False', '!='];

export const SingleFieldTypes = ['Is Null', 'Is Not Null', 'Is Null Or Empty', 'Is Not Null And Is Not Empty',
    'Is True', 'Is False'];

export const EMPHIERARCHY = ['EMPCOMPANY', 'EMPTEAM', 'EMPAGENT'];

export const CONTACTHIERARCHY = ['CONCOMPANY', 'CONCAMPAIGN', 'CONSKILL', 'CONPOINTOFCONTACT'];

export const EMPHIERARCHYKEY = ['EMPGROUP3ID', 'EMPGROUP2ID', 'EMPGROUP1ID'];

export const CONTACTHIERARCHYKEY = ['RECGROUP4ID', 'RECGROUP3ID', 'RECGROUP2ID', 'RECGROUP1ID'];

export const OperationSymbols = {
    'Modulus': '%',
    'Exponent': '^',
    'Divide': '÷',
    'Multiply': '×',
    "Minus": '-',
    "Plus": '+',
    "GreaterThan": '>',
    "LessThan": '<',
    "EqualTo": '='
}

export interface GroupSubjectType {
    selectedSectionName: string;
    isNew: boolean;
}

export interface DataItem {
    Name: string;
    JoinID?: number;
    StringComponents?: any[];
    DefaultValue?: any;
    DataType: number;
    ExpectedType?: number;
};

export interface HierarchyItem {
    dropdownOptions: string;
    FeedName: string;
    DataList: DataItem[];
};

export const BaseGroupType = [
    'Employee',
    'Contact'
]

export const HierarchySourceSystem = 'Hierarchy';

export const CommonHierarchyDropdownData = [
    {
        "Name": "GroupID",
        "key": "ItemID",
        "DataType": 1
    },
    {
        "Name": "ParentGroupID",
        "key": "ParentID",
        "DataType": 1
    }
]

export const ConvertFieldTypes = {
    "ItemID": "GroupID",
    "ParentID": "ParentGroupID"
}

export const DESIRED_HIERARCHY_ORDER = ['Company (Employee)', 'Team (Employee)', 'Agent (Employee)', 'Company (Contact)', 'Campaign (Contact)', 'Skill (Contact)', 'Point of Contact (Contact)'];

export const DataFlowSubFields = ['rightOperand', 'leftOperand', 'months', 'days', 'hours', 'minutes', 'seconds', 'comparisons', 'positive', 'negative', 'formulas'];
